import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { getInventoryUsers } from '../../api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Users, RefreshCw, ShieldCheck, ShieldAlert, Search, UserCheck, UserX, Boxes,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  admin: 'Admin',
  hr_manager: 'HR Manager',
  warehouse_manager: 'Warehouse Manager',
  state_head: 'State Head',
  asm: 'ASM',
  tl: 'Team Lead',
  fse: 'FSE',
};

const roleLabel = (v) => ROLE_LABELS[v] || v || '-';

const InventoryUsersAccess = ({ user, setUser }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [role, setRole] = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [accessFilter, setAccessFilter] = useState('all'); // all | ok | missing

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { active_only: activeOnly };
      if (role && role !== 'all') params.role = role;
      const r = await getInventoryUsers(params);
      setRows(r.data.rows || []);
      setSummary(r.data.summary || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [role, activeOnly]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = rows.filter((r) => {
    if (accessFilter === 'ok' && !r.inventory_access_check) return false;
    if (accessFilter === 'missing' && r.inventory_access_check) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = [
        r.name, r.email, r.employee_id, r.designation, r.city,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="inventory-users-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-indigo-600" />
              Inventory Users &amp; Access
            </h1>
            <p className="text-sm text-slate-500">
              All employees mapped to inventory roles — verify registration and access.
            </p>
          </div>
          <Button onClick={load} variant="outline" data-testid="refresh-btn" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* KPI CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card data-testid="kpi-total">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500">Total Users</p>
              <p className="text-2xl font-bold text-indigo-700">{summary.total_users || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-active">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500 flex items-center gap-1"><UserCheck className="h-3 w-3" />Active</p>
              <p className="text-2xl font-bold text-green-700">{summary.active_users || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-holdings">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500 flex items-center gap-1"><Boxes className="h-3 w-3" />With Holdings</p>
              <p className="text-2xl font-bold text-purple-700">{summary.with_holdings || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-access-ok">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />Access OK</p>
              <p className="text-2xl font-bold text-teal-700">{summary.access_ok_count || 0}</p>
            </CardContent>
          </Card>
          <Card data-testid="kpi-access-missing">
            <CardContent className="pt-5">
              <p className="text-xs text-slate-500 flex items-center gap-1"><ShieldAlert className="h-3 w-3" />Missing Mapping</p>
              <p className="text-2xl font-bold text-red-700">{summary.access_missing_count || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* FILTERS */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="h-3 w-3 absolute left-2 top-2.5 text-slate-400" />
                  <Input
                    className="pl-7"
                    placeholder="Name, email, employee ID, city..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger data-testid="filter-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {Object.entries(ROLE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Access</Label>
                <Select value={accessFilter} onValueChange={setAccessFilter}>
                  <SelectTrigger data-testid="filter-access"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="ok">Access OK</SelectItem>
                    <SelectItem value="missing">Missing Mapping</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Show</Label>
                <Select value={activeOnly ? 'active' : 'all'} onValueChange={(v) => setActiveOnly(v === 'active')}>
                  <SelectTrigger data-testid="filter-active"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active only</SelectItem>
                    <SelectItem value="all">Include Exited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end text-xs text-slate-500">
                Showing <b className="mx-1">{filteredRows.length}</b> of <b className="mx-1">{rows.length}</b> users
              </div>
            </div>
          </CardContent>
        </Card>

        {/* USER TABLE */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 text-xs font-medium">Name / Email</th>
                    <th className="text-left p-2 text-xs font-medium">Employee ID</th>
                    <th className="text-left p-2 text-xs font-medium">Role</th>
                    <th className="text-left p-2 text-xs font-medium">Designation</th>
                    <th className="text-left p-2 text-xs font-medium">City</th>
                    <th className="text-center p-2 text-xs font-medium">Active</th>
                    <th className="text-right p-2 text-xs font-medium">Holdings</th>
                    <th className="text-center p-2 text-xs font-medium">Access</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${r.id}`}>
                      <td className="p-2">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.email || '(no email)'}</div>
                      </td>
                      <td className="p-2 font-mono text-xs text-indigo-700">{r.employee_id || '-'}</td>
                      <td className="p-2">
                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-100 text-indigo-700">
                          {roleLabel(r.role)}
                        </span>
                      </td>
                      <td className="p-2 text-xs">{r.designation || '-'}</td>
                      <td className="p-2 text-xs">{r.city || '-'}</td>
                      <td className="p-2 text-center">
                        {r.is_active ? (
                          <UserCheck className="h-4 w-4 text-green-600 inline" />
                        ) : (
                          <UserX className="h-4 w-4 text-red-500 inline" />
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <div className="text-indigo-700 font-semibold">{r.serialized_holdings || 0}</div>
                        {r.bulk_holdings > 0 && (
                          <div className="text-[10px] text-purple-600">+{r.bulk_holdings} bulk</div>
                        )}
                      </td>
                      <td className="p-2 text-center" title={r.access_reason}>
                        {r.inventory_access_check ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">
                            <ShieldCheck className="h-3 w-3" />OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">
                            <ShieldAlert className="h-3 w-3" />Missing
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan="8" className="p-8 text-center text-slate-500">
                        {loading ? 'Loading...' : 'No matching users'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default InventoryUsersAccess;
